import { create } from 'zustand';
import {
  ListPaymentObligations,
  CreatePaymentObligation,
  GeneratePaymentObligations,
  GetPaymentObligation,
  UpdatePaymentObligation,
  DeletePaymentObligation,
  GetOutstandingBalance,
  PaymentObligation,
  CreatePaymentObligationI,
  GenerateObligationsI,
  UpdatePaymentObligationI
} from '../api/api';
import toast from 'react-hot-toast';

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetPaymentObligationsResponse {
  data: {
    obligations: PaymentObligation[];
    pagination?: PaginationInfo;
  };
}

interface PaymentObligationResponse {
  data: {
    paymentObligation: PaymentObligation;
  };
  status: boolean;
  message: string;
}

interface GenerateObligationsResponse {
  data: {
    obligations: PaymentObligation[];
    errors?: Array<{ period: string; error: string }>;
    totalGenerated: number;
  };
  status: boolean;
  message: string;
}

interface OutstandingBalanceResponse {
  data: {
    familyId: string;
    paymentTypeId: string;
    totalDue: number;
    totalPaid: number;
    remainingBalance: number;
    obligations: PaymentObligation[];
    summary: {
      pending: number;
      partial: number;
      paid: number;
      total: number;
    };
  };
  status: boolean;
  message: string;
}

interface DeletePaymentObligationResponse {
  status: boolean;
  message: string;
}

interface PaymentObligationStore {
  obligations: PaymentObligation[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getPaymentObligations: (
    search?: string,
    page?: number,
    limit?: number,
    paymentTypeId?: string,
    familyId?: string,
    status?: string,
    period?: string,
    startPeriod?: string,
    endPeriod?: string
  ) => Promise<void>;
  createPaymentObligation: (obligation: CreatePaymentObligationI) => Promise<void>;
  generateObligations: (data: GenerateObligationsI) => Promise<PaymentObligation[] | null>;
  getPaymentObligationById: (id: string) => Promise<PaymentObligation | null>;
  updatePaymentObligation: (id: string, obligation: UpdatePaymentObligationI) => Promise<void>;
  deletePaymentObligation: (id: string) => Promise<void>;
  getOutstandingBalance: (
    familyId: string,
    paymentTypeId: string
  ) => Promise<OutstandingBalanceResponse['data'] | null>;
}

export const usePaymentObligationStore = create<PaymentObligationStore>((set, get) => ({
  obligations: [] as PaymentObligation[],
  loading: false,
  error: null,
  pagination: null,

  getPaymentObligations: async (
    search?: string,
    page?: number,
    limit?: number,
    paymentTypeId?: string,
    familyId?: string,
    status?: string,
    period?: string,
    startPeriod?: string,
    endPeriod?: string
  ) => {
    set({ loading: true, error: null });
    const { data, error } = await ListPaymentObligations<GetPaymentObligationsResponse>(
      search,
      page,
      limit,
      paymentTypeId,
      familyId,
      status,
      period,
      startPeriod,
      endPeriod
    );

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      obligations: data.data.obligations || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createPaymentObligation: async (obligation: CreatePaymentObligationI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreatePaymentObligation<PaymentObligationResponse>(obligation);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create payment obligation' });
      return;
    }

    const obligations = get().obligations;
    set({ obligations: [data.data.paymentObligation, ...obligations], loading: false });
    toast.success('Payment obligation created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPaymentObligations(
      undefined,
      currentPagination?.page,
      currentPagination?.limit
    );
  },

  generateObligations: async (data: GenerateObligationsI) => {
    set({ loading: true, error: null });
    const { data: response, error } = await GeneratePaymentObligations<GenerateObligationsResponse>(data);

    if (error || !response.status) {
      set({ loading: false, error: (error as string) || 'Failed to generate obligations' });
      return null;
    }

    if (response.data.errors && response.data.errors.length > 0) {
      const errorMessages = response.data.errors.map((e) => `${e.period}: ${e.error}`).join(', ');
      toast.error(`Some obligations failed to generate: ${errorMessages}`);
    } else {
      toast.success(`Successfully generated ${response.data.totalGenerated} obligation(s)`);
    }

    set({ loading: false });
    // Refresh the list
    const currentPagination = get().pagination;
    get().getPaymentObligations(
      undefined,
      currentPagination?.page,
      currentPagination?.limit
    );
    return response.data.obligations || null;
  },

  getPaymentObligationById: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetPaymentObligation<PaymentObligationResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get payment obligation' });
      return null;
    }

    set({ loading: false });
    return data.data.paymentObligation;
  },

  updatePaymentObligation: async (id: string, obligation: UpdatePaymentObligationI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdatePaymentObligation<PaymentObligationResponse>(id, obligation);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update payment obligation' });
      return;
    }

    const obligations = get().obligations;
    const updatedObligations = obligations.map((o) =>
      o._id === id ? { ...o, ...data.data.paymentObligation } : o
    );
    set({ obligations: updatedObligations, loading: false });
    toast.success('Payment obligation updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPaymentObligations(
      undefined,
      currentPagination?.page,
      currentPagination?.limit
    );
  },

  deletePaymentObligation: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeletePaymentObligation<DeletePaymentObligationResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete payment obligation' });
      return;
    }

    const obligations = get().obligations.filter((o) => o._id !== id);
    set({ obligations, loading: false });
    toast.success('Payment obligation deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPaymentObligations(
      undefined,
      currentPagination?.page,
      currentPagination?.limit
    );
  },

  getOutstandingBalance: async (familyId: string, paymentTypeId: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetOutstandingBalance<OutstandingBalanceResponse>(
      familyId,
      paymentTypeId
    );

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get outstanding balance' });
      return null;
    }

    set({ loading: false });
    return data.data;
  },
}));

export type {
  PaymentObligation,
  CreatePaymentObligationI,
  GenerateObligationsI,
  UpdatePaymentObligationI
};
